/**
 * Composable for accessing pre-generated static data.
 *
 * Static data is generated at build time by scripts/generate-static-data.js
 * and placed in public/static-data/. This composable loads and caches the
 * data, providing instant access for rendering page headers while full
 * API data loads in the background.
 *
 * In Capacitor, the fetch reads from the local app bundle (< 10ms).
 * On web, it reads from the same origin (single network hop, cacheable).
 */

export interface StaticSeasonData {
    id: number;
    name: string;
    api_id?: number;
    is_current?: boolean;
    start?: string;
    end?: string;
    image?: string;
    image_light?: string;
    image_dark?: string;
    league?: {
        id: number;
        name: string;
        logo_path?: string;
        country?: {
            id: number;
            name: string;
            image_path?: string;
        };
    } | null;
    country?: {
        id?: number;
        name?: string;
    } | null;
    historic_seasons?: Array<{ id: number; name: string }> | null;
}

export interface StaticTeamData {
    id: number;
    name: string;
    short_code?: string;
    logo_path?: string;
    logo?: string;
}

type DataMap<T> = Record<string, T>;

// Singleton cache shared across all component instances
let seasonsData: DataMap<StaticSeasonData> | null = null;
let teamsData: DataMap<StaticTeamData> | null = null;
let seasonsPromise: Promise<DataMap<StaticSeasonData>> | null = null;
let teamsPromise: Promise<DataMap<StaticTeamData>> | null = null;

async function loadSeasons(): Promise<DataMap<StaticSeasonData>> {
    if (seasonsData) {
        return seasonsData;
    }
    if (seasonsPromise) {
        return seasonsPromise;
    }

    seasonsPromise = fetch("/static-data/seasons.json")
        .then((res) => {
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            return res.json();
        })
        .then((data: DataMap<StaticSeasonData>) => {
            seasonsData = data;
            return data;
        })
        .catch(() => {
            // Static data is optional — gracefully degrade
            seasonsData = {};
            return {} as DataMap<StaticSeasonData>;
        })
        .finally(() => {
            seasonsPromise = null;
        });

    return seasonsPromise;
}

async function loadTeams(): Promise<DataMap<StaticTeamData>> {
    if (teamsData) {
        return teamsData;
    }
    if (teamsPromise) {
        return teamsPromise;
    }

    teamsPromise = fetch("/static-data/teams.json")
        .then((res) => {
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            return res.json();
        })
        .then((data: DataMap<StaticTeamData>) => {
            teamsData = data;
            return data;
        })
        .catch(() => {
            teamsData = {};
            return {} as DataMap<StaticTeamData>;
        })
        .finally(() => {
            teamsPromise = null;
        });

    return teamsPromise;
}

export function useStaticData() {
    /**
     * Get pre-generated static season data by ID.
     * Returns null if not found (season wasn't included in last build).
     */
    async function getStaticSeason(
        id: number,
    ): Promise<StaticSeasonData | null> {
        const data = await loadSeasons();
        return data[id] || null;
    }

    /**
     * Get pre-generated static team data by ID.
     * Returns null if not found (team wasn't included in last build).
     */
    async function getStaticTeam(id: number): Promise<StaticTeamData | null> {
        const data = await loadTeams();
        return data[id] || null;
    }

    /**
     * Preload all static data into memory cache.
     * Call early in the app lifecycle for best performance.
     * Non-blocking — fires off fetch requests without awaiting.
     */
    function preloadStaticData(): void {
        loadSeasons();
        loadTeams();
    }

    return {
        getStaticSeason,
        getStaticTeam,
        preloadStaticData,
    };
}
